import type { z } from 'zod';

import type { HotelDetailsSchema } from './traveler_view';

export type HotelCancellationPolicy = z.infer<typeof HotelDetailsSchema>['cancellationPolicy'];

/** A leitura ÚNICA da política de cancelamento do hotel emitido — o modal de
 *  cancelamento, o documento do voucher e o agente de trip dizem a mesma data a
 *  partir daqui. */
export interface HotelCancellationState {
  /** Até quando cancelar não custa nada. `null` quando não dá para afirmar. */
  freeUntil: Date | null;
  /** A multa de fato (`value > 0`); `0`/ausente não é cobrança. */
  penalty: number | null;
  /** A cobrança já vale hoje — só então a multa se anuncia e exige ciência. */
  inPenaltyWindow: boolean;
}

/** O balcão do hotel, não Greenwich: "hoje" é o dia-calendário em -03:00. */
const HOTEL_OFFSET_MS = -3 * 60 * 60 * 1000;

/** As datas da política são dia-só à meia-noite UTC; "hoje" vem do relógio. */
function dayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** A política chega como UMA janela de MULTA: "cancelar entre `startAt` e
 *  `endAt` custa `value`" — `endAt` cai ≈ no check-in. Então o grátis acaba
 *  quando a multa COMEÇA (`startAt`), a mesma regra de
 *  `hotelFreeCancellationUntil` na busca e a mesma data do voucher legado.
 *  Sem multa (`value` null/0) a janela é de cortesia e o grátis vai até `endAt`.
 *  Multa sem `startAt` não promete prazo e vale desde já: sub-afirmar é seguro,
 *  super-afirmar mente sobre dinheiro. */
export function hotelCancellationState(
  policy: HotelCancellationPolicy | undefined,
  now: Date,
): HotelCancellationState | null {
  if (!policy) return null;
  const penalty = policy.value != null && policy.value > 0 ? policy.value : null;
  if (penalty == null) {
    return policy.endAt ? { freeUntil: policy.endAt, penalty, inPenaltyWindow: false } : null;
  }
  const freeUntil = policy.startAt ?? null;
  const today = dayKey(new Date(now.getTime() + HOTEL_OFFSET_MS));
  const inPenaltyWindow = freeUntil == null || today >= dayKey(freeUntil);
  return { freeUntil, penalty, inPenaltyWindow };
}
